import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { apiErrorText } from 'Utils/errorText';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import {
  getGraphNode, updateDataGraph, tableLabel, graphHasHopLimit, DEFAULT_MAX_HOPS, graphUpdateBody, withRowFilter,
  OUTCOMES, graphOutcome, objectsProblem, withObject, getDraftReport, getRuleSuggestions,
} from './dataGraphApi';
import ObjectListEditor from './ObjectListEditor';
import { KindToggles, withoutHiddenKinds } from './objectKinds';
import { NeededPanel, FindingsList, reportNoun } from './ObjectReport';

/**
 * Where a source keeps the key reads schema.table / column for a database, database.collection /
 * field path for MongoDB, Object / field API name for Salesforce. Same value, different home: the
 * placeholder says which one the consultant is in.
 */
export const keyTablePlaceholder = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Contact';
  return e === 'mongodb' ? 'portal.members' : 'public.person';
};
export const keyColumnPlaceholder = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Person_Source_Value__c (field API name)';
  return e === 'mongodb' ? 'person_source_value (field path)' : 'person_source_value';
};

/** The draft check waits this long after the last edit before asking the platform. */
export const DRAFT_DEBOUNCE_MS = 500;

const formOf = (g) => ({
  name: (g && g.name) || '',
  description: (g && g.description) || '',
  keyName: (g && g.keyName) || '',
  keyTable: (g && g.keyTable) || '',
  keyColumn: (g && g.keyColumn) || '',
  maxHops: g && g.maxHops != null ? String(g.maxHops) : '',
  outcome: graphOutcome(g),
  objects: ((g && g.objects) || []).map((o) => ({ ...o, rules: (o.rules || []).map((r) => ({ ...r })) })),
});

const lower = (s) => String(s || '').toLowerCase();

// A data graph's own settings, above its explorer: the name subsets pick it by; the key — its table is
// where a subset's row selection starts when this source is the parent (the subset editor shows it
// read-only), and with other sources rows are matched on it (the parent's key name is the subset's
// shared key name); and what the graph asks for (contract 3.0, 2026-11-03): EVERYTHING CONNECTED to the
// key table (relational: to a hop limit; MongoDB and Salesforce walk without one), or A LIST of objects,
// each reached by rules (ObjectListEditor). Beside the list, "Needed by this list" (the platform's draft
// isolation report, re-asked as the settings are edited): what a provision will do with every lookup to
// an object off the list. Table names come from the graph's node list, columns from the node API.
const GraphConfigPanel = ({ graph, connection, tables, canEdit, onSaved, testReport, onDraftReport }) => {
  const [form, setForm] = useState(formOf(graph));
  const [columns, setColumns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState({ report: null, error: null, loading: false });
  const [suggest, setSuggest] = useState({ items: null, loading: false, error: null });
  // The key table picker: every profiled object, narrowed by a search and the kind toggles.
  const [keySearch, setKeySearch] = useState('');
  const [keyHiddenKinds, setKeyHiddenKinds] = useState(() => new Set());
  // A row filter saved elsewhere on the page also replaces `graph`: follow it, but never over
  // settings the consultant is still editing here.
  const shown = useRef(formOf(graph));
  useEffect(() => {
    const next = formOf(graph);
    setForm((f) => (JSON.stringify(f) === JSON.stringify(shown.current) ? next : f));
    shown.current = next;
  }, [graph]);

  const engine = (connection && (connection.type || connection.engine)) || (graph && graph.engine);
  const noun = reportNoun(engine);
  const hopLimited = graphHasHopLimit(connection || { datasourceType: graph && graph.datasourceType });
  const list = form.outcome === 'LIST';
  const dataSourceId = graph && graph.dataSourceId;

  // Columns per table, read once each from the node API (the key picker and every rule share them).
  const columnCache = useRef(new Map());
  useEffect(() => { columnCache.current = new Map(); }, [dataSourceId]);
  const columnsOf = useCallback((name) => {
    const key = lower(name);
    if (!key || dataSourceId == null) return Promise.resolve([]);
    if (!columnCache.current.has(key)) {
      const node = (tables || []).find((n) => lower(tableLabel(n)) === key);
      const dot = String(name).indexOf('.');
      const table = node ? node.table : (dot > 0 ? String(name).slice(dot + 1) : String(name));
      const schema = node ? node.schema : (dot > 0 ? String(name).slice(0, dot) : undefined);
      columnCache.current.set(key, getGraphNode(dataSourceId, table, schema)
        .then((d) => ((d && d.columns) || []).map((c) => c.name))
        .catch(() => { columnCache.current.delete(key); return []; }));
    }
    return columnCache.current.get(key);
  }, [dataSourceId, tables]);

  const keyNode = useMemo(
    () => (tables || []).find((n) => lower(tableLabel(n)) === lower(form.keyTable.trim())),
    [tables, form.keyTable],
  );
  useEffect(() => {
    if (!graph || !form.keyTable.trim()) { setColumns([]); return undefined; }
    let alive = true;
    columnsOf(form.keyTable.trim()).then((c) => { if (alive) setColumns(c || []); });
    return () => { alive = false; };
  }, [graph, form.keyTable, columnsOf]);

  // React 16 pools the event: read the value now, not inside the updater.
  const set = (k) => (e) => { const { value } = e.target; setForm((f) => ({ ...f, [k]: value })); setNotice(null); };
  const setObjects = (objects) => { setForm((f) => ({ ...f, objects })); setNotice(null); };
  const halfKey = Boolean(form.keyTable.trim()) !== Boolean(form.keyColumn.trim());
  const hops = form.maxHops.trim();
  const showHops = hopLimited && !list;
  const badHops = showHops && hops !== '' && !/^\d+$/.test(hops);
  const listProblem = list
    ? (!form.keyTable.trim() || !form.keyColumn.trim()
      ? 'A list needs a key table and column: the selection starts there.'
      : objectsProblem(form.objects, form.keyTable.trim()))
    : null;
  const dirty = JSON.stringify(form) !== JSON.stringify(formOf(graph));

  const bodyOf = useCallback((f) => ({
    dataSourceId: graph.dataSourceId,
    name: f.name.trim(),
    description: f.description.trim() || null,
    keyName: f.keyName.trim() || null,
    keyTable: f.keyTable.trim() || null,
    keyColumn: f.keyColumn.trim() || null,
    // kept while the graph is a list (the planner ignores it there), so flipping back loses nothing
    maxHops: hopLimited && /^\d+$/.test(f.maxHops.trim()) ? Number(f.maxHops.trim()) : null,
    outcome: f.outcome,
    objects: f.objects.map((o) => ({
      name: o.name.trim(),
      save: o.save !== false,
      rules: (o.rules || []).map((r) => ({
        column: String(r.column || '').trim(), source: r.source, sourceColumn: String(r.sourceColumn || '').trim(),
        maxPerParent: r.maxPerParent != null && r.maxPerParent !== '' ? Number(r.maxPerParent) : null,
      })),
    })),
  }), [graph, hopLimited]);

  // The draft check (contract 3.0): the isolation report of what the editor holds, saved or not, from the plan
  // alone — "Needed by this list" before the Test. Re-asked DRAFT_DEBOUNCE_MS after the last edit that
  // changes the plan (name and description do not); skipped while the settings could not be saved anyway.
  const planKey = JSON.stringify([form.outcome, form.keyTable.trim(), form.keyColumn.trim(), showHops ? hops : '', form.objects]);
  const canDraft = Boolean(graph) && Boolean(form.keyTable.trim()) && Boolean(form.keyColumn.trim()) && !halfKey && !badHops && !listProblem;
  const draftSeq = useRef(0);
  useEffect(() => {
    if (!canDraft) { setDraft({ report: null, error: null, loading: false }); return undefined; }
    const seq = draftSeq.current + 1;
    draftSeq.current = seq;
    setDraft((d) => ({ ...d, loading: true }));
    const t = setTimeout(() => {
      getDraftReport({ graphId: graph.id, ...bodyOf(form), tableFilters: graph.tableFilters || [] })
        .then((report) => { if (draftSeq.current === seq) setDraft({ report: report || null, error: null, loading: false }); })
        .catch((e) => { if (draftSeq.current === seq) setDraft({ report: null, error: apiErrorText(e, 'Could not check the list'), loading: false }); });
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // planKey stands for every field of `form` the plan reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, canDraft, graph]);
  useEffect(() => { if (onDraftReport) onDraftReport(draft.report); }, [draft.report, onDraftReport]);

  const askSuggestions = async () => {
    setSuggest({ items: null, loading: true, error: null });
    try {
      const items = await getRuleSuggestions({ graphId: graph.id, ...bodyOf(form), tableFilters: graph.tableFilters || [] });
      setSuggest({ items, loading: false, error: null });
    } catch (e) {
      setSuggest({ items: null, loading: false, error: apiErrorText(e, 'Could not suggest rules') });
    }
  };

  // The last Test's per-rule counts, by rule id, to show beside each rule (only while the Test is the saved graph's).
  const ruleCounts = useMemo(() => {
    const out = {};
    if (!testReport || !testReport.counted) return out;
    (testReport.objects || []).forEach((o) => (o.rules || []).forEach((r) => { if (r && r.id) out[r.id] = r; }));
    return out;
  }, [testReport]);

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const saved = await updateDataGraph(graph.id, bodyOf(form));
      setNotice({ tone: 'ok', text: 'Saved. Subsets that use this data graph pick it up on their next preview or run.' });
      if (onSaved) onSaved(saved);
    } catch (e) {
      setNotice({ tone: 'error', text: apiErrorText(e, 'Could not save the data graph') });
    } finally {
      setBusy(false);
    }
  };

  const removeFilter = async (table) => {
    setBusy(true);
    setNotice(null);
    try {
      const saved = await updateDataGraph(graph.id, graphUpdateBody(graph, withRowFilter(graph, table, '')));
      if (onSaved) onSaved(saved);
    } catch (e) {
      setNotice({ tone: 'error', text: apiErrorText(e, 'Could not remove the row filter') });
    } finally {
      setBusy(false);
    }
  };

  const disabled = !canEdit;
  const listed = useMemo(() => new Set(form.objects.map((o) => lower(o.name))), [form.objects]);
  const tableNames = (tables || []).map((n) => tableLabel(n));
  const keyInProfile = !form.keyTable.trim() || tableNames.length === 0 || Boolean(keyNode);
  const keyQuery = lower(keySearch.trim());
  const keyChoices = withoutHiddenKinds(tables, keyHiddenKinds).map((n) => tableLabel(n))
    .filter((n) => !keyQuery || lower(n).includes(keyQuery));
  // The chosen key stays pickable whatever the search and toggles hide.
  if (keyNode && !keyChoices.some((n) => lower(n) === lower(form.keyTable.trim()))) keyChoices.unshift(tableLabel(keyNode));
  const neededPanel = (
    <div className='dg-needed-side' data-testid='dg-config-needed'>
      {draft.report && <FindingsList findings={draft.report.findings} testId='dg-config-findings' />}
      {draft.report
        ? (
          <NeededPanel needed={draft.report.needed} counted={Boolean(draft.report.counted)} listed={listed} noun={noun}
            onAdd={list && !disabled ? (name) => setObjects(withObject(form.objects, name)) : null} testId='dg-config-needed-list' />
        )
        : (
          <div className='dg-muted' data-testid='dg-config-needed-idle'>
            <div className='dg-section__title'>Needed by this list</div>
            {draft.loading ? 'Checking…' : (canDraft ? 'Nothing to show yet.' : 'Set the key table and column to see what the list needs.')}
          </div>
        )}
      {draft.error && <div className='dg-error' data-testid='dg-config-needed-error'>{draft.error}</div>}
    </div>
  );

  return (
    <div className='dg-panel mb-3' data-testid='dg-config'>
      <div className='dg-panel__head'>
        <div className='dg-panel__title'>Data graph settings</div>
        <div className='dg-muted'>What a subset pulls when it picks this data graph for {connection ? connection.name : 'this source'}.</div>
      </div>
      <div className='dg-config'>
        <label className='dg-config__field'>Name
          <input className='dg-input' value={form.name} onChange={set('name')} disabled={disabled} data-testid='dg-config-name' />
        </label>
        <label className='dg-config__field' title='Shown as the shared key of a subset whose parent uses this data graph, e.g. "Member number"'>Key name
          <input className='dg-input' value={form.keyName} onChange={set('keyName')} disabled={disabled} placeholder='Member number'
            data-testid='dg-config-key-name' />
        </label>
        <label className='dg-config__field' title={'Where a subset’s row selection starts when this source is its parent; with other sources, the table rows are matched on'}>Key table
          {tableNames.length > 0 ? (
            <span className='dg-picker'>
              {!disabled && (
                <input className='dg-input' value={keySearch} onChange={(e) => setKeySearch(e.target.value)}
                  placeholder={`Search ${tableNames.length} objects…`} aria-label='Search the key table'
                  data-testid='dg-config-key-table-search' />
              )}
              <select className='dg-input' value={form.keyTable} onChange={set('keyTable')} disabled={disabled} data-testid='dg-config-key-table'>
                <option value=''>No key</option>
                {form.keyTable && !keyNode && <option value={form.keyTable}>{form.keyTable} (not in the latest profile)</option>}
                {keyChoices.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {!disabled && (
                <KindToggles items={tables} hidden={keyHiddenKinds} onChange={setKeyHiddenKinds} testId='dg-config-key-kinds' />
              )}
            </span>
          ) : (
            <input className='dg-input' value={form.keyTable} onChange={set('keyTable')} disabled={disabled}
              placeholder={keyTablePlaceholder(engine)} data-testid='dg-config-key-table' />
          )}
        </label>
        <label className='dg-config__field'>Key column
          {columns.length > 0 ? (
            <select className='dg-input' value={form.keyColumn} onChange={set('keyColumn')} disabled={disabled} data-testid='dg-config-key-column'>
              <option value=''>column…</option>
              {form.keyColumn && !columns.includes(form.keyColumn) && <option value={form.keyColumn}>{form.keyColumn} (not in the latest profile)</option>}
              {columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input className='dg-input' value={form.keyColumn} onChange={set('keyColumn')} disabled={disabled}
              placeholder={keyColumnPlaceholder(engine)} data-testid='dg-config-key-column' />
          )}
        </label>
        {showHops && (
          <label className='dg-config__field' title='How many steps from the key table the walk follows child tables; what the reached rows reference is always included'>Max child hops
            <input className='dg-input' type='number' min='0' max='64' value={form.maxHops} onChange={set('maxHops')} disabled={disabled}
              placeholder={`${DEFAULT_MAX_HOPS} (default)`} data-testid='dg-config-max-hops' />
          </label>
        )}
        {!hopLimited && !list && (
          <div className='dg-config__field' data-testid='dg-config-no-hops'>Max child hops
            <span className='dg-muted'>No limit: the walk follows the shared key and this graph&apos;s links.</span>
          </div>
        )}
      </div>
      <label className='dg-config__field mt-2'>Description
        <input className='dg-input' value={form.description} onChange={set('description')} disabled={disabled}
          placeholder='What this data graph is for' data-testid='dg-config-description' />
      </label>
      {!keyInProfile && (
        <div className='dg-error' data-testid='dg-config-key-missing'>{form.keyTable} is not in the latest profile: re-sync the schema, or pick another key table.</div>
      )}
      {(graph.warnings || []).length > 0 && (
        <ul className='dg-warnings' data-testid='dg-config-warnings'>
          {graph.warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : (w.message || w.code)}</li>)}
        </ul>
      )}
      <div className='dg-outcome' role='radiogroup' aria-label='What this data graph pulls' data-testid='dg-config-outcome'>
        {OUTCOMES.map(([v, label]) => (
          <label key={v}>
            <input type='radio' name={`dg-outcome-${graph.id}`} value={v} checked={form.outcome === v} disabled={disabled}
              onChange={set('outcome')} data-testid={`dg-config-outcome-${v}`} />
            {label}
          </label>
        ))}
      </div>
      <div className='dg-muted dg-note mt-1' data-testid='dg-config-outcome-help'>
        {list
          ? 'Only the objects listed below are saved: each is reached by its rules, and what the saved rows look up among them comes along for integrity.'
          : `Everything linked below the key table${showHops ? ' to the hop limit' : ''}, plus what those rows reference.`}
      </div>
      {list ? (
        <div className='dg-listedit'>
          <ObjectListEditor objects={form.objects} onChange={setObjects} keyTable={form.keyTable.trim()} tables={tables}
            columnsOf={columnsOf} disabled={disabled} counts={ruleCounts} suggestions={suggest.items}
            onSuggest={disabled ? null : askSuggestions} suggesting={suggest.loading} suggestError={suggest.error} />
          {neededPanel}
        </div>
      ) : (
        <div className='mt-2'>{neededPanel}</div>
      )}
      {(graph.tableFilters || []).length > 0 && (
        <div className='mt-2' data-testid='dg-config-filters'>
          <div className='dg-config__field'>Row filters</div>
          {graph.tableFilters.map((f) => (
            <div className='dg-filter__row' key={f.table} data-testid={`dg-config-filter-${f.table}`}>
              <span className='dg-mono'>{f.table}</span>
              <span className='dg-mono dg-muted dg-clip' title={f.expression}>{f.expression}</span>
              {canEdit && (
                <Button size='sm' variant='link' className='p-0' disabled={busy} data-testid={`dg-config-filter-remove-${f.table}`}
                  onClick={() => removeFilter(f.table)}>Remove</Button>
              )}
            </div>
          ))}
        </div>
      )}
      {halfKey && <div className='dg-error' data-testid='dg-config-half-key'>A key needs both a table and a column, or neither.</div>}
      {badHops && <div className='dg-error'>Max child hops is a whole number from 0 to 64.</div>}
      {listProblem && !halfKey && <div className='dg-error' data-testid='dg-config-list-problem'>{listProblem}</div>}
      <div className='dg-config__actions'>
        <Button size='sm' onClick={save} data-testid='dg-config-save'
          disabled={disabled || busy || !dirty || !form.name.trim() || halfKey || badHops || Boolean(listProblem)}
          title={disabled ? READ_ONLY_TITLE : undefined}>
          {busy ? 'Saving…' : 'Save settings'}
        </Button>
        {notice && <span className={notice.tone === 'error' ? 'dg-error mt-0' : 'dg-muted'} data-testid='dg-config-notice'>{notice.text}</span>}
      </div>
    </div>
  );
};

export default GraphConfigPanel;
